import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, FileText, Receipt, Calculator, Users, FolderOpen,
  CloudUpload, LineChart, Mail, MonitorSmartphone, FolderKanban, ClipboardList,
} from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";

const OFFICE_IMG = "https://images.pexels.com/photos/8353764/pexels-photo-8353764.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const proServices = [
  { icon: FileText, title: "Gestion administrative", text: "Suivi des dossiers, courriers, relations avec les organismes et partenaires." },
  { icon: Receipt, title: "Facturation & devis", text: "Émission, suivi et relance de vos factures pour une trésorerie saine." },
  { icon: Calculator, title: "Pré-comptabilité", text: "Préparation et classement des pièces pour votre expert-comptable." },
  { icon: Users, title: "Assistance RH", text: "Contrats, dossiers du personnel, suivi administratif de vos salariés." },
  { icon: FolderOpen, title: "Organisation documentaire", text: "Classement, archivage et arborescence claire de vos documents." },
  { icon: CloudUpload, title: "Dématérialisation", text: "Passage au numérique avec des outils simples et sécurisés." },
  { icon: LineChart, title: "Suivi d'activité", text: "Tableaux de bord et indicateurs pour piloter votre activité sereinement." },
];

const indivServices = [
  { icon: FolderKanban, title: "Dossiers administratifs", text: "Constitution et suivi de vos dossiers auprès des organismes." },
  { icon: MonitorSmartphone, title: "Démarches en ligne", text: "Accompagnement pas à pas sur les plateformes et services numériques." },
  { icon: Mail, title: "Courriers & rédaction", text: "Rédaction et envoi de vos courriers administratifs." },
  { icon: FolderOpen, title: "Organisation de documents", text: "Tri, classement et mise en ordre de vos papiers et fichiers." },
  { icon: ClipboardList, title: "Formalités du quotidien", text: "Un coup de main pour toutes les démarches qui s'accumulent." },
];

const ServiceCard = ({ s, dark }) => (
  <motion.div
    whileHover={{ y: -4 }}
    transition={{ duration: 0.3 }}
    className={`rounded-2xl p-8 border transition-shadow duration-300 hover:shadow-lg ${
      dark ? "bg-argia-ink border-white/10" : "bg-white border-[#2C2A29]/10"
    }`}
  >
    <s.icon size={26} strokeWidth={1.5} className={dark ? "text-argia-gold" : "text-argia-red"} />
    <h3 className={`mt-5 font-display text-xl font-bold ${dark ? "text-white" : "text-argia-ink"}`}>{s.title}</h3>
    <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-white/70" : "text-argia-muted"}`}>{s.text}</p>
  </motion.div>
);

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="relative overflow-hidden grain">
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-16 lg:px-10 lg:pt-24">
          <Reveal>
            <SectionLabel>Services</SectionLabel>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-argia-ink sm:text-5xl lg:text-6xl" data-testid="services-title">
              Un accompagnement sur mesure, pour les pros comme pour les particuliers
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-argia-muted md:text-lg">
              Mission ponctuelle ou accompagnement régulier : chaque prestation est adaptée à vos besoins réels,
              sur site ou à distance.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pros */}
      <section className="bg-argia-ink py-24 grain relative" data-testid="services-pros">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-argia-gold">
              <span className="h-px w-8 bg-argia-gold" />
              Pour les professionnels
            </span>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Artisans, commerçants, indépendants, professions libérales, associations, TPE
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/70">
              Je prends en charge la gestion administrative de votre activité pour vous permettre de vous
              consacrer pleinement à votre métier.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {proServices.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <ServiceCard s={s} dark />
              </Reveal>
            ))}
            <Reveal delay={0.4}>
              <div className="relative h-full min-h-[220px] overflow-hidden rounded-2xl">
                <img src={OFFICE_IMG} alt="Bureau épuré et organisé" className="absolute inset-0 h-full w-full object-cover" data-testid="services-office-image" />
                <div className="absolute inset-0 bg-gradient-to-t from-argia-ink/80 to-transparent" />
                <p className="absolute bottom-6 left-6 right-6 font-display text-xl italic text-white">
                  « Donnez de la clarté à votre administratif »
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Particuliers */}
      <section className="bg-argia-stone py-24" data-testid="services-particuliers">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <SectionLabel>Pour les particuliers</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold tracking-tight text-argia-ink sm:text-4xl">
              Des démarches simplifiées, en toute sérénité
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-argia-muted">
              Les démarches administratives peuvent sembler complexes ou difficiles à gérer seul.
              Je vous accompagne pour vous simplifier la vie et vous faire gagner du temps.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {indivServices.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <ServiceCard s={s} />
              </Reveal>
            ))}
            <Reveal delay={0.35}>
              <Link
                to="/rendez-vous"
                data-testid="services-cta-card"
                className="group flex h-full min-h-[220px] flex-col items-start justify-center rounded-2xl bg-argia-red p-8 transition-all duration-300 hover:bg-argia-redDark hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="font-display text-2xl font-bold text-white">Un besoin particulier ?</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/80">
                  Parlons-en lors d'un premier rendez-vous découverte, offert et sans engagement.
                </p>
                <span className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-argia-red">
                  Prendre rendez-vous
                  <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
